import { useCallback, useEffect, useState } from "react";
import { Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import {
  FaSearch,
  FaCheck,
  FaTimes,
  FaDownload,
  FaEdit,
  FaPause,
  FaEye,
} from "react-icons/fa";
import "../../tds-style.css";
import TdsDialoge from "../../../../../../components/Dialog/tdsDialoge";
import Pagination from "../../../../../../components/pagination/pagination.js";

// Redux
import { getEmployeePayrollRecordsPath } from "../../../../../../services/config";
import { getTdsEmpListData, initTdsEmpList } from "../../../../../../redux/slices/tdsSlice";
import { useDispatch } from "react-redux";
import { useSelect } from "../../../../../../services/commonServices";

const EmployeeManagement = () => {
  useEffect(() => {
    localStorage.removeItem("tdsUser");
  }, []);

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [filters, setFilters] = useState({
    status: {
      submitted: true,
      underReview: true,
      needProof: false,
      approved: false,
    },
    financialYear: "2023-24",
  });

  const [selectedLocations, setSelectedLocations] = useState({});
  const [selectedDepartments, setSelectedDepartments] = useState({});
  const [selectedMissingProofs, setSelectedMissingProofs] = useState({});
  const [selectedHighRisks, setSelectedHighRisks] = useState({});

  const [totalRecords, setTotalRecords] = useState(0);
  const [pendingRecords, setPendingRecords] = useState(0);
  const [highRiskCount, setHighRiskCount] = useState(0);
  const [locationStats, setLocationStats] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [missingProofsStats, setMissingProofsStats] = useState([]);
  const [highRiskStats, setHighRiskStats] = useState([]);

  const user = JSON.parse(localStorage.getItem("user_data")) || {};
  const [staffId, setStaffId] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [isRequestEdit, setIsRequestEdit] = useState(false);
  const [isHold, setIsHold] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [quickPresets, setQuickPresets] = useState("3 weeks");

  const dispatch = useDispatch();
  const tdsStore = useSelect("tds");

  const [tdsEmpList, setTdsEmpList] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Relative time formatter
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString();
  };

  const getLastAction = (status) => {
    const map = {
      Ready: "Verification complete",
      Review: "Under review",
      New: "Form submitted",
      Issues: "Issues flagged",
    };
    return map[status] || "Status updated";
  };

  const formatEmployeeData = (apiData) => {
    if (!Array.isArray(apiData)) return [];

    return apiData.map((emp) => {
      const name =
        emp.staffName ||
        emp.employeeName ||
        emp.name ||
        `${emp.firstName || ""} ${emp.lastName || ""}`.trim() ||
        "Unknown Employee";

      const nameParts = name.split(" ");
      const avatar = `${nameParts[0]?.[0] || ""}${nameParts[1]?.[0] || ""}`.toUpperCase() || "EE";

      const empId = emp.employeeId || emp.empId || emp.staffId || emp.employeeCode || "N/A";
      const pan = emp.pan || emp.panNumber || emp.panCardNumber || "Not Provided";

      let status = "New";
      const statusVal = (emp.tdsStatus || emp.status || "").toString().toLowerCase();
      if (statusVal.includes("approved") || statusVal.includes("ready")) status = "Ready";
      else if (statusVal.includes("review")) status = "Review";
      else if (statusVal.includes("issue") || statusVal.includes("pending")) status = "Issues";

      const missingProofs = Array.isArray(emp.missingProofs)
        ? emp.missingProofs.filter(Boolean)
        : [];

      const netTds = Number(emp.netTds || emp.tdsAmount || emp.totalTds || 0);
      const fy = emp.financialYear || emp.fy || "2023-24";
      const location = emp.branch || emp.location || emp.branchName || "Unknown";
      const department = emp.department || emp.departmentName || "Unknown";

      const actionDate = emp.updatedAt || emp.submittedDate || emp.createdAt || new Date();
      const isHighRisk = emp.isHighRisk || missingProofs.length > 2;

      return {
        id: emp.staffId || emp._id || emp.id,
        staffId: emp.staffId || emp._id || emp.id,
        name,
        empId,
        pan,
        fy,
        status,
        missingProofs,
        netTds,
        lastAction: getLastAction(status),
        lastActionTime: formatRelativeTime(actionDate),
        avatar,
        location,
        department,
        isHighRisk,
        submittedDate: emp.submittedDate || actionDate,
        verified: !!emp.verified,
      };
    });
  };

  const calculateStats = (employees) => {
    if (!employees.length) return;

    setTotalRecords(employees.length);
    setPendingRecords(employees.filter((e) => e.status !== "Ready").length);
    setHighRiskCount(employees.filter((e) => e.isHighRisk).length);

    const locMap = employees.reduce((acc, e) => {
      if (e.location && e.location !== "Unknown") acc[e.location] = (acc[e.location] || 0) + 1;
      return acc;
    }, {});
    setLocationStats(Object.entries(locMap).map(([n, c]) => ({ name: n, count: c })));

    const deptMap = employees.reduce((acc, e) => {
      if (e.department && e.department !== "Unknown") acc[e.department] = (acc[e.department] || 0) + 1;
      return acc;
    }, {});
    setDepartmentStats(Object.entries(deptMap).map(([n, c]) => ({ name: n, count: c })));

    const proofMap = employees.reduce((acc, e) => {
      e.missingProofs.forEach((p) => (acc[p] = (acc[p] || 0) + 1));
      return acc;
    }, {});
    setMissingProofsStats(Object.entries(proofMap).map(([n, c]) => ({ name: n, count: c })));

    const riskTypes = {
      "Multiple Missing Proofs": employees.filter((e) => e.missingProofs.length > 2).length,
      "High TDS Amount": employees.filter((e) => e.netTds > 50000).length,
      "Long Pending": employees.filter((e) => {
        const days = (Date.now() - new Date(e.submittedDate)) / 86400000;
        return days > 45;
      }).length,
    };
    setHighRiskStats(
      Object.entries(riskTypes)
        .filter(([, c]) => c > 0)
        .map(([n, c]) => ({ name: n, count: c }))
    );
  };

  const getEmpRecordList = useCallback(() => {
    dispatch(
      getTdsEmpListData({
        path: getEmployeePayrollRecordsPath,
        data: { userId: user.user_id },
      })
    );
  }, [dispatch, user.user_id]);

  useEffect(() => {
    getEmpRecordList();
  }, [getEmpRecordList]);

  useEffect(() => {
    if (tdsStore.tdsEmpListSuccess) {
      setLoading(false);
      const formatted = formatEmployeeData(tdsStore.tdsEmpListData);
      setTdsEmpList(formatted);
      setFilteredEmployees(formatted);
      calculateStats(formatted);
      if (formatted.length > 0) setStaffId(formatted[0].staffId);
      dispatch(initTdsEmpList());
    } else if (tdsStore.tdsEmpListError) {
      setLoading(false);
      setError("Failed to load employee data.");
    } else if (tdsStore.tdsEmpListLoading) {
      setLoading(true);
    }
  }, [tdsStore, dispatch]);

  const applyFilters = () => {
    let filtered = [...tdsEmpList];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.empId.toLowerCase().includes(q) ||
          e.pan.toLowerCase().includes(q)
      );
    }

    const activeStatuses = [];
    if (filters.status.submitted) activeStatuses.push("New", "Review");
    if (filters.status.underReview) activeStatuses.push("Review");
    if (filters.status.approved) activeStatuses.push("Ready");
    if (filters.status.needProof) {
      filtered = filtered.filter((e) => e.missingProofs.length > 0);
    }
    if (activeStatuses.length > 0) {
      filtered = filtered.filter((e) => activeStatuses.includes(e.status));
    }

    const locs = Object.keys(selectedLocations).filter((k) => selectedLocations[k]);
    if (locs.length) filtered = filtered.filter((e) => locs.includes(e.location));

    const depts = Object.keys(selectedDepartments).filter((k) => selectedDepartments[k]);
    if (depts.length) filtered = filtered.filter((e) => depts.includes(e.department));

    const proofs = Object.keys(selectedMissingProofs).filter((k) => selectedMissingProofs[k]);
    if (proofs.length) {
      filtered = filtered.filter((e) => e.missingProofs.some((p) => proofs.includes(p)));
    }

    const risks = Object.keys(selectedHighRisks).filter((k) => selectedHighRisks[k]);
    if (risks.length) {
      filtered = filtered.filter((e) => {
        if (risks.includes("Multiple Missing Proofs")) return e.missingProofs.length > 2;
        if (risks.includes("High TDS Amount")) return e.netTds > 50000;
        if (risks.includes("Long Pending")) {
          const days = (Date.now() - new Date(e.submittedDate)) / 86400000;
          return days > 45;
        }
        return false;
      });
    }

    if (filters.financialYear) {
      filtered = filtered.filter((e) => e.fy === filters.financialYear);
    }

    setFilteredEmployees(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [
    searchQuery,
    filters,
    selectedLocations,
    selectedDepartments,
    selectedMissingProofs,
    selectedHighRisks,
    tdsEmpList,
  ]);

  const handleSelectAll = () => {
    setSelectedEmployees(
      selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0
        ? []
        : filteredEmployees.map((e) => e.id)
    );
  };

  const handleSelectEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const calculateNetTds = () =>
    filteredEmployees
      .filter((e) => selectedEmployees.includes(e.id))
      .reduce((sum, e) => sum + e.netTds, 0);

  const getStatusBadgeClass = (s) => {
    switch (s) {
      case "Review": return "badge-review";
      case "Ready": return "badge-ready";
      case "New": return "badge-new";
      case "Issues": return "badge-issues";
      default: return "";
    }
  };

  const getProofBadgeClass = (p) => {
    if (/landlord|math/i.test(p)) return "badge-landlord";
    if (/receipt/i.test(p)) return "badge-receipts";
    if (/insurance/i.test(p)) return "badge-insurance";
    if (/hra/i.test(p)) return "badge-hra";
    if (/80c/i.test(p)) return "badge-80c";
    return "badge-default";
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilters({
      status: { submitted: true, underReview: true, needProof: false, approved: false },
      financialYear: "2023-24",
    });
    setSelectedLocations({});
    setSelectedDepartments({});
    setSelectedMissingProofs({});
    setSelectedHighRisks({});
    setSelectedEmployees([]);
  };

  // Export to CSV Function
  const exportToCSV = () => {
    if (selectedEmployees.length === 0) {
      alert("Please select at least one employee to export.");
      return;
    }

    const selectedData = filteredEmployees.filter(emp => selectedEmployees.includes(emp.id));

    const headers = [
      "Employee Name",
      "Employee ID",
      "PAN",
      "Financial Year",
      "Status",
      "Missing Proofs",
      "Net TDS (₹)",
      "Last Action",
      "Submitted Date"
    ];

    const rows = selectedData.map(emp => [
      emp.name,
      emp.empId,
      emp.pan,
      emp.fy,
      emp.status,
      emp.missingProofs.join("; ") || "None",
      emp.netTds.toLocaleString("en-IN"),
      emp.lastAction,
      new Date(emp.submittedDate).toLocaleDateString("en-IN")
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `TDS_Employees_Export_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const actionCounts = {
    approve: selectedEmployees.filter((id) => filteredEmployees.find((e) => e.id === id)?.status === "Ready").length,
    edit: selectedEmployees.filter((id) => filteredEmployees.find((e) => e.id === id)?.status === "Review").length,
    hold: selectedEmployees.filter((id) => filteredEmployees.find((e) => e.id === id)?.status === "Issues").length,
  };

  // Status filter counts
  const statusCounts = {
    submitted: tdsEmpList.filter((e) => ["New", "Review"].includes(e.status)).length,
    underReview: tdsEmpList.filter((e) => e.status === "Review").length,
    needProof: tdsEmpList.filter((e) => e.missingProofs.length > 0).length,
    approved: tdsEmpList.filter((e) => e.status === "Ready").length,
  };

  if (loading) {
    return (
      <div className="employee-management">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading employee data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-management">
        <div className="alert alert-danger m-3" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!tdsEmpList.length) {
    return (
      <div className="employee-management">
        <div className="text-center py-5">
          <p className="text-muted">No TDS employee data available.</p>
          <Button onClick={getEmpRecordList} className="mt-3">
            Retry Loading Data
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-management">
      <Row className="">
        {/* Sidebar - Original Style */}
        <Col lg={3} xl={3} className="sidebar-col">
          <div className="sidebar">
            <div className="mb-4">
              <InputGroup>
                <InputGroup.Text className="search-icon">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </InputGroup>
            </div>

            <Row className="g-3 mb-4">
              <Col xs={6}>
                <div className="stat-card">
                  <div className="stat-number">{totalRecords}</div>
                  <div className="stat-label">Total Records</div>
                </div>
              </Col>
              <Col xs={6}>
                <div className="stat-card">
                  <div className="stat-number stat-selected">{selectedEmployees.length}</div>
                  <div className="stat-label">Selected</div>
                </div>
              </Col>
              <Col xs={6}>
                <div className="stat-card">
                  <div className="stat-number stat-pending">{pendingRecords}</div>
                  <div className="stat-label">Pending</div>
                </div>
              </Col>
              <Col xs={6}>
                <div className="stat-card">
                  <div className="stat-number stat-high-risk">{highRiskCount}</div>
                  <div className="stat-label">High Risk</div>
                </div>
              </Col>
            </Row>

            <div className="filters-section">
              <h6 className="filter-title">Filters</h6>

              <div className="mb-3">
                <div className="filter-category-label">Status</div>
                <div className="filter-options">
                  {["submitted", "underReview", "needProof", "approved"].map((key) => {
                    const labels = { submitted: "Submitted", underReview: "Under Review", needProof: "Need Proof", approved: "Approved" };
                    return (
                      <div className="filter-item" key={key}>
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={filters.status[key]}
                            onChange={(e) => setFilters((p) => ({
                              ...p,
                              status: { ...p.status, [key]: e.target.checked },
                            }))}
                          />
                          <span className="filter-name">{labels[key]}</span>
                        </div>
                        <span className="filter-count">{statusCounts[key]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mb-3">
                <div className="filter-category-label">Financial Year</div>
                <Form.Select
                  className="custom-select"
                  value={filters.financialYear}
                  onChange={(e) => setFilters((p) => ({ ...p, financialYear: e.target.value }))}
                >
                  <option value="2023-24">FY 2023-24</option>
                  <option value="2024-25">FY 2024-25</option>
                  <option value="2025-26">FY 2025-26</option>
                </Form.Select>
              </div>

              {locationStats.length > 0 && (
                <div className="mb-3">
                  <div className="filter-category-label">Location</div>
                  <div className="filter-options">
                    {locationStats.map((loc) => (
                      <div className="filter-item" key={loc.name}>
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={!!selectedLocations[loc.name]}
                            onChange={(e) => setSelectedLocations((p) => ({ ...p, [loc.name]: e.target.checked }))}
                          />
                          <span className="filter-name">{loc.name}</span>
                        </div>
                        <span className="filter-count">{loc.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {departmentStats.length > 0 && (
                <div className="mb-3">
                  <div className="filter-category-label">Department</div>
                  <div className="filter-options">
                    {departmentStats.map((dept) => (
                      <div className="filter-item" key={dept.name}>
                        <div 
                          className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={!!selectedDepartments[dept.name]}
                            onChange={(e) => setSelectedDepartments((p) => ({ ...p, [dept.name]: e.target.checked }))}
                          />
                          <span className="filter-name">{dept.name}</span>
                        </div>
                        <span className="filter-count">{dept.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {missingProofsStats.length > 0 && (
                <div className="mb-3">
                  <div className="filter-category-label">Missing Proofs</div>
                  <div className="filter-options">
                    {missingProofsStats.map((proof) => (
                      <div className="filter-item" key={proof.name}>
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={!!selectedMissingProofs[proof.name]}
                            onChange={(e) => setSelectedMissingProofs((p) => ({ ...p, [proof.name]: e.target.checked }))}
                          />
                          <span className="filter-name">{proof.name}</span>
                        </div>
                        <span className="filter-count">{proof.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* High Risk Cases - Always shown */}
              <div className="mb-3">
                <div className="filter-category-label">High Risk Cases</div>
                <div className="filter-options">
                  {highRiskStats.length > 0 ? (
                    highRiskStats.map((risk) => (
                      <div className="filter-item" key={risk.name}>
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={!!selectedHighRisks[risk.name]}
                            onChange={(e) => setSelectedHighRisks((p) => ({ ...p, [risk.name]: e.target.checked }))}
                          />
                          <span className="filter-name">{risk.name}</span>
                        </div>
                        <span className="filter-count">{risk.count}</span>
                      </div>
                    ))
                  ) : (
                    <div className="filter-item text-muted">
                      No high-risk cases detected
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <Button className="btn-clear w-100" onClick={handleClearFilters}>
                <FaTimes className="me-2" />
                Clear All
              </Button>
            </div>
          </div>
        </Col>

        {/* Main Content - Original Style */}
        <Col lg={9} xl={9} className="overflow-x-scroll">
          <div className="main-content">
            <div className="action-bar">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
                  onChange={handleSelectAll}
                  className="me-2"
                />
                <span className="select-text">
                  Select All · {selectedEmployees.length} of {filteredEmployees.length} selected
                </span>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button className="btn-approve" onClick={() => setIsApproved(true)}>
                  <FaCheck className="me-2" />
                  Approve All
                </Button>
                <Button className="btn-edit" onClick={() => setIsRequestEdit(true)}>
                  <FaEdit className="me-2" />
                  Request Edit
                </Button>
                <Button className="btn-hold" onClick={() => setIsHold(true)}>
                  <FaPause className="me-2" />
                  Put On Hold
                </Button>
                <Button className="btn-reject" onClick={() => setIsRejected(true)}>
                  <FaTimes className="me-2" />
                  Reject
                </Button>
                <Button className="btn-export" onClick={exportToCSV}>
                  <FaDownload className="me-2" />
                  Export Selected ({selectedEmployees.length})
                </Button>
              </div>
            </div>

            <div className="table-container">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th className="th-checkbox">
                      <Form.Check
                        type="checkbox"
                        checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th style={{ width: "300px" }}>Employee Name</th>
                    <th style={{ width: "100px" }}>PAN</th>
                    <th style={{ width: "90px" }}>FY</th>
                    <th style={{ width: "100px" }}>Status</th>
                    <th style={{ width: "250px" }}>Missing Proofs</th>
                    <th style={{ width: "100px" }}>Net TDS</th>
                    <th style={{ width: "200px" }}>Last Action</th>
                    <th style={{ width: "50px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((employee) => (
                    <tr key={employee.id} className="table-row">
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => handleSelectEmployee(employee.id)}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="employee-avatar">{employee.avatar}</div>
                          <div>
                            <div className="employee-name">{employee.name}</div>
                            <div className="employee-id">{employee.empId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="td-text">{employee.pan}</td>
                      <td className="td-text">{employee.fy}</td>
                      <td>
                        <div className={`status-badge ${getStatusBadgeClass(employee.status)}`}>
                          {employee.status}
                        </div>
                      </td>
                      <td>
                        {employee.missingProofs.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {employee.missingProofs.map((proof, idx) => (
                              <div
                                key={idx}
                                className={`proof-badge ${getProofBadgeClass(proof)}`}
                              >
                                {proof}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-custom">None</span>
                        )}
                      </td>
                      <td className="td-tds">₹{employee.netTds.toLocaleString()}</td>
                      <td>
                        <div>
                          <div className="last-action">{employee.lastAction}</div>
                          <div className="last-action-time">{employee.lastActionTime}</div>
                        </div>
                      </td>
                      <td>
                        <Button variant="link" className="btn-icon">
                          <FaEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredEmployees.length > 0 ? (
                <Pagination
                  data={filteredEmployees}
                  setCurrentData={setCurrentData}
                  className={"bottom-pagination-absolute"}
                />
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No employees found matching your criteria.</p>
                </div>
              )}
            </div>

            <div className="summary-bar mt-3">
              <div className="d-flex align-items-center gap-4">
                <div>
                  <span className="summary-text">
                    {selectedEmployees.length} employees selected
                  </span>
                </div>
                <div>
                  <span className="summary-text">Net TDS Impact: </span>
                  <span className="summary-amount">
                    ₹{calculateNetTds().toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="summary-text">
                Actions to apply:{" "}
                <span className="summary-approve">Approve ({actionCounts.approve})</span>,{" "}
                <span className="summary-edit">Request Edit ({actionCounts.edit})</span>,{" "}
                <span className="summary-hold">Hold ({actionCounts.hold})</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <TdsDialoge
        staffId={staffId}
        isApproved={isApproved}
        closeApproveDialog={() => setIsApproved(false)}
        isRequestEdit={isRequestEdit}
        closeRequestEditDialog={() => setIsRequestEdit(false)}
        quickPresets={quickPresets}
        setQuickPresets={setQuickPresets}
        isHold={isHold}
        closeHoldDialog={() => setIsHold(false)}
        isRejected={isRejected}
        closeRejectDialog={() => setIsRejected(false)}
      />
    </div>
  );
};

export default EmployeeManagement;